import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactElement, type ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { AppHeader } from '../components/layout/AppHeader';
import { UserMenu } from '../components/layout/UserMenu';
import { BooksNav } from '../components/navigation/BooksNav';
import { BooksAccountingPanel } from '../components/panels/BooksAccountingPanel';
import { LiveDoiPanel } from '../components/panels/LiveDoiPanel';
import { CitationMonthDemo } from './CitationMonthDemo';
import { AdoptionMastersPanel, type AdoptionSection } from '../components/panels/AdoptionMastersPanel';
import { ActivityTable } from '../components/activity/ActivityTable';
import { EXCALIBUR_API_URL, PERIODS_API_URL, SEARCH_API_URL } from '../constants';
import { activityFromAudit } from '../domain/activityLog';
import { hasPermission, permissionsOf } from '../domain/formatters';
import type { LoginResponse } from '../domain/types';

type AuthFetch = (url: string, init?: RequestInit) => Promise<Response>;

interface AccountingPageProps {
  auth: LoginResponse;
  onLogout: () => void | Promise<void>;
  authFetch: AuthFetch;
}

interface PeriodRow {
  id: string;
  periodId: string;
  year: number;
  month: number;
  productionDate: string;
  paymentDate: string;
  operatingRegion: string;
  status: string;
}

interface AuditRow {
  id: string;
  actor: string;
  occurredAtUtc: string;
  action: string;
  beforeJson?: string;
  afterJson?: string;
}

interface SearchHit {
  kind: string;
  id: string;
  label: string;
  secondary?: string;
}

interface WellHeader {
  id: string;
  apiNumber: string;
  name: string;
  fieldName: string;
  operatingRegion: string;
  citationIsOperator: boolean;
  excaliburCostCenter?: string;
  pilot: boolean;
}

interface CompareLine {
  wellApi: string;
  wellId: string;
  status: string;
  externalKey: string;
  interestClass: string;
  oursDecimal?: string;
  excaliburDecimal?: string;
  reasonCode?: string;
  explained: boolean;
}

interface CompareResult {
  wells: WellHeader[];
  lines: CompareLine[];
  unexplainedCount: number;
}

interface IngestBatch {
  id: string;
  kind: string;
  status: string;
  acceptedCount: number;
  exceptionCount: number;
  exceptions: { externalKey: string; reason: string }[];
}

const NAME_FIXTURE = {
  rows: [
    { externalKey: 'EXC-OWNER-001', legalName: 'Demo Mineral Owner' },
    { externalKey: 'EXC-OP-001', legalName: 'Citation Oil & Gas Corp.' },
    { externalKey: 'EXC-EXTRA-001', legalName: 'Excalibur Extra Owner' },
    { externalKey: 'EXC-DUP-1', legalName: 'Demo Mineral Owner' }
  ]
};

const WELL_FIXTURE = {
  rows: [
    { externalKey: 'W-42123400010000', apiNumber: '42123400010000', costCenter: 'XC-100', pilot: true },
    { externalKey: 'W-42123400020000', apiNumber: '42123400020000', costCenter: 'XC-200', pilot: true },
    { externalKey: 'W-42123400030000', apiNumber: '42123400030000', costCenter: 'XC-300', pilot: true },
    { externalKey: 'W-BAD', apiNumber: '42999999999999', costCenter: 'XC-BAD', pilot: true }
  ]
};

const DOI_FIXTURE = {
  rows: [
    { wellApi: '42123400010000', partyExternalKey: 'EXC-OWNER-001', interestClass: 'Royalty', decimal: '0.12500000' },
    { wellApi: '42123400020000', partyExternalKey: 'EXC-OWNER-001', interestClass: 'Royalty', decimal: '0.49999999' },
    { wellApi: '42123400030000', partyExternalKey: 'EXC-OWNER-001', interestClass: 'Royalty', decimal: '0.12500000' },
    { wellApi: '42123400030000', partyExternalKey: 'EXC-EXTRA-001', interestClass: 'OverridingRoyalty', decimal: '0.01000000' }
  ]
};

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

function tabClass(active: boolean): string {
  return active ? 'app-main-nav-link app-main-nav-link--active' : 'app-main-nav-link';
}

function statusClass(status: string): string {
  const s = status.trim().toLowerCase();
  if (s === 'open') {
    return 'status-pill status-active';
  }
  if (s === 'softclosed') {
    return 'status-pill status-draft';
  }
  return 'status-pill';
}

export function AccountingPage(props: AccountingPageProps): ReactElement {
  const { auth, onLogout, authFetch } = props;
  const { pathname } = useLocation();
  const perms = permissionsOf(auth);
  const canRead = hasPermission(perms, 'doi:read', auth.role);
  const canWriteDoi = hasPermission(perms, 'doi:update', auth.role);
  const canUpdatePeriod = hasPermission(perms, 'period:update', auth.role);
  const canImport = hasPermission(perms, 'excalibur:import', auth.role);
  const isCompare = pathname.startsWith('/accounting/compare');
  const isIngest = pathname.startsWith('/accounting/ingest');
  const isLive = pathname.startsWith('/accounting/doi');
  const isBooks = pathname.startsWith('/accounting/books');
  const isDemo = pathname.startsWith('/accounting/demo');
  const isAccounts = pathname.startsWith('/accounting/accounts');
  const isAfes = pathname.startsWith('/accounting/afes');
  const isIndexes = pathname.startsWith('/accounting/indexes');
  const isPpa = pathname.startsWith('/accounting/ppa');
  const adoptionSection: AdoptionSection | null = isAccounts
    ? 'accounts'
    : isAfes
      ? 'afes'
      : isIndexes
        ? 'indexes'
        : isPpa
          ? 'ppa'
          : null;

  const [periods, setPeriods] = useState<PeriodRow[]>([]);
  const [periodId, setPeriodId] = useState('');
  const [error, setError] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [searched, setSearched] = useState(false);
  const [searchHits, setSearchHits] = useState<{ wells: SearchHit[]; parties: SearchHit[]; contracts: SearchHit[] }>({
    wells: [],
    parties: [],
    contracts: []
  });
  const [selectedWell, setSelectedWell] = useState<WellHeader | null>(null);
  const [selectedContract, setSelectedContract] = useState<{ id: string; label: string } | null>(null);

  const loadPeriods = useCallback(async () => {
    const rows = await readJson<PeriodRow[]>(await authFetch(PERIODS_API_URL));
    setPeriods(rows);
    const stored = sessionStorage.getItem('cca-period-id');
    const open = rows.find((p) => p.status === 'Open');
    const next = rows.find((p) => p.id === stored) ?? open ?? rows[0];
    if (next) {
      setPeriodId(next.id);
      sessionStorage.setItem('cca-period-id', next.id);
    }
  }, [authFetch]);

  useEffect(() => {
    if (!canRead) {
      return;
    }
    void loadPeriods().catch((e) => setError(e instanceof Error ? e.message : 'Failed to load periods'));
  }, [canRead, loadPeriods]);

  useEffect(() => {
    if (!isDemo || periods.length === 0) {
      return;
    }
    const current = periods.find((p) => p.id === periodId);
    const citationOpen = [...periods]
      .filter((p) => p.status === 'Open' && p.year < 2080)
      .sort((a, b) => b.periodId.localeCompare(a.periodId))[0];
    if (citationOpen && (!current || current.year >= 2080)) {
      setPeriodId(citationOpen.id);
      sessionStorage.setItem('cca-period-id', citationOpen.id);
    }
  }, [isDemo, periodId, periods]);

  useEffect(() => {
    if (!canRead || !isLive || selectedContract) {
      return;
    }
    void (async () => {
      try {
        const data = await readJson<{ contracts: SearchHit[] }>(
          await authFetch(`${SEARCH_API_URL}?q=${encodeURIComponent('T-01')}`)
        );
        const hit = data.contracts[0];
        if (hit) {
          setSelectedContract({ id: hit.id, label: hit.label });
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not load T-01');
      }
    })();
  }, [authFetch, canRead, isLive, selectedContract]);

  const currentPeriod = periods.find((p) => p.id === periodId);

  const onSearch = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const q = searchQ.trim();
      const data = await readJson<{ wells: SearchHit[]; parties: SearchHit[]; contracts: SearchHit[] }>(
        await authFetch(`${SEARCH_API_URL}?q=${encodeURIComponent(q)}`)
      );
      setSearched(true);
      setSearchHits(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    }
  };

  if (!canRead) {
    return (
      <Shell auth={auth} onLogout={onLogout} periodLabel={currentPeriod?.periodId}>
        <p className="error" data-testid="accounting-denied">
          You do not have permission to view accounting.
        </p>
      </Shell>
    );
  }

  return (
    <Shell auth={auth} onLogout={onLogout} periodLabel={currentPeriod?.periodId}>
      <div className="books-toolbar">
        <nav className="app-main-nav" aria-label="Accounting">
          <Link
            to="/accounting"
            className={tabClass(
              !isCompare && !isIngest && !isLive && !isBooks && !isDemo && adoptionSection === null
            )}
          >
            Periods
          </Link>
          <Link to="/accounting/demo" className={tabClass(isDemo)} data-testid="accounting-demo-tab">
            Director demo
          </Link>
          <Link to="/accounting/doi" className={tabClass(isLive)} data-testid="accounting-live-doi-tab">
            Live DOI
          </Link>
          <Link to="/accounting/books" className={tabClass(isBooks)} data-testid="accounting-books-tab">
            Books
          </Link>
          <Link to="/accounting/accounts" className={tabClass(isAccounts)} data-testid="accounting-accounts-tab">
            Accounts
          </Link>
          <Link to="/accounting/afes" className={tabClass(isAfes)} data-testid="accounting-afes-tab">
            AFEs
          </Link>
          <Link to="/accounting/indexes" className={tabClass(isIndexes)} data-testid="accounting-indexes-tab">
            Indexes
          </Link>
          <Link to="/accounting/ppa" className={tabClass(isPpa)} data-testid="accounting-ppa-tab">
            PPA
          </Link>
          <Link to="/accounting/compare" className={tabClass(isCompare)} data-testid="accounting-compare-tab">
            Compare DOI
          </Link>
          {canImport && (
            <Link to="/accounting/ingest" className={tabClass(isIngest)} data-testid="accounting-ingest-tab">
              Ingest
            </Link>
          )}
        </nav>
        <label className="grid">
          Period
          <select
            data-testid="period-switcher"
            value={periodId}
            onChange={(e) => {
              setPeriodId(e.target.value);
              sessionStorage.setItem('cca-period-id', e.target.value);
            }}
          >
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.periodId} ({p.status})
              </option>
            ))}
          </select>
        </label>
        <form className="grid" onSubmit={onSearch}>
          <input
            data-testid="global-search"
            placeholder="Search well, party, or lease"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
          />
          <button type="submit" data-testid="global-search-submit">
            Search
          </button>
        </form>
      </div>
      {error && <p className="error">{error}</p>}
      {selectedWell && <PropertyHeader well={selectedWell} />}
      {isLive && selectedContract && (
        <section className="card" data-testid="doi-lease-header">
          <h2>{selectedContract.label}</h2>
          <p>As-of the selected production month. Snapshot compare stays on Compare DOI.</p>
        </section>
      )}
      <SearchResults
        hits={searchHits}
        searched={searched}
        onOpenWell={(hit) => {
          const api = hit.label.match(/\((\d+)\)/)?.[1] ?? '';
          setSelectedWell({
            id: hit.id,
            apiNumber: api,
            name: hit.label.replace(/\s*\(\d+\)\s*$/, ''),
            fieldName: '',
            operatingRegion: hit.secondary ?? '',
            citationIsOperator: true,
            pilot: true
          });
        }}
        onOpenContract={isLive ? (hit) => setSelectedContract({ id: hit.id, label: hit.label }) : undefined}
      />
      {isLive && selectedContract && currentPeriod && (
        <LiveDoiPanel
          authFetch={authFetch}
          contractId={selectedContract.id}
          asOf={currentPeriod.productionDate}
          canWrite={canWriteDoi}
        />
      )}
      {isBooks && (
        <BooksAccountingPanel
          authFetch={authFetch}
          canPost={canUpdatePeriod}
          periodId={currentPeriod?.periodId ?? ''}
          productionDate={currentPeriod?.productionDate ?? new Date().toISOString().slice(0, 10)}
          error={error}
          setError={setError}
        />
      )}
      {isDemo && (
        <CitationMonthDemo
          authFetch={authFetch}
          canPrepare={canUpdatePeriod}
          periodId={currentPeriod?.periodId ?? ''}
          productionDate={currentPeriod?.productionDate ?? new Date().toISOString().slice(0, 10)}
          onError={setError}
        />
      )}
      {adoptionSection && (
        <AdoptionMastersPanel
          section={adoptionSection}
          authFetch={authFetch}
          canWrite={canUpdatePeriod}
          periodId={currentPeriod?.periodId ?? ''}
          onError={setError}
        />
      )}
      {!isCompare && !isIngest && !isLive && !isBooks && !isDemo && !adoptionSection && (
        <PeriodsPanel
          authFetch={authFetch}
          periods={periods}
          canUpdate={canUpdatePeriod}
          onReload={loadPeriods}
          onError={setError}
        />
      )}
      {isCompare && (
        <ComparePanel
          authFetch={authFetch}
          onSelectWell={setSelectedWell}
          selectedWell={selectedWell}
        />
      )}
      {isIngest && !canImport && <Navigate to="/accounting" replace />}
      {isIngest && canImport && (
        <IngestPanel authFetch={authFetch} periodId={periodId} onError={setError} />
      )}
    </Shell>
  );
}

function Shell({
  auth,
  onLogout,
  periodLabel,
  children
}: {
  auth: LoginResponse;
  onLogout: () => void | Promise<void>;
  periodLabel?: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="page page-authed">
      <AppHeader
        navSlot={<BooksNav />}
        endSlot={
          <UserMenu
            auth={auth}
            onLogout={onLogout}
            extraPills={periodLabel ? <span className="user-pill">Period {periodLabel}</span> : undefined}
          />
        }
      />
      <div className="app-content">{children}</div>
    </div>
  );
}

function PropertyHeader({ well }: { well: WellHeader }): ReactElement {
  return (
    <section className="card" data-testid="property-header">
      <h2>{well.name}</h2>
      <p>
        API {well.apiNumber || '—'} · Field {well.fieldName || '—'} · Operator{' '}
        {well.citationIsOperator ? 'Citation operated' : 'Non-operated'} · Excalibur {well.excaliburCostCenter || '—'}
      </p>
    </section>
  );
}

function SearchResults({
  hits,
  searched,
  onOpenWell,
  onOpenContract
}: {
  hits: { wells: SearchHit[]; parties: SearchHit[]; contracts: SearchHit[] };
  searched: boolean;
  onOpenWell: (hit: SearchHit) => void;
  onOpenContract?: (hit: SearchHit) => void;
}): ReactElement | null {
  const empty = hits.wells.length + hits.parties.length + hits.contracts.length === 0;
  if (!searched) {
    return null;
  }
  return (
    <section className="card" data-testid="search-results">
      <h2>Search results</h2>
      <table className="requests-grid">
        <thead>
          <tr>
            <th>Kind</th>
            <th>Label</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {empty && (
            <tr>
              <td colSpan={3}>No matching wells, parties, or leases</td>
            </tr>
          )}
          {hits.wells.map((h) => (
            <tr key={`w-${h.id}`}>
              <td>Well</td>
              <td>
                {h.label}
                {h.secondary ? ` · ${h.secondary}` : ''}
              </td>
              <td>
                <button type="button" onClick={() => onOpenWell(h)}>
                  Open header
                </button>
              </td>
            </tr>
          ))}
          {hits.parties.map((h) => (
            <tr key={`p-${h.id}`}>
              <td>Party</td>
              <td>
                {h.label}
                {h.secondary ? ` · ${h.secondary}` : ''}
              </td>
              <td></td>
            </tr>
          ))}
          {hits.contracts.map((h) => (
            <tr key={`c-${h.id}`}>
              <td>Lease</td>
              <td>
                {h.label}
                {h.secondary ? ` · ${h.secondary}` : ''}
              </td>
              <td>
                {onOpenContract ? (
                  <button type="button" onClick={() => onOpenContract(h)}>
                    Use for DOI
                  </button>
                ) : (
                  <Link to={`/contracts/${h.id}`}>Open</Link>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function PeriodsPanel({
  authFetch,
  periods,
  canUpdate,
  onReload,
  onError
}: {
  authFetch: AuthFetch;
  periods: PeriodRow[];
  canUpdate: boolean;
  onReload: () => Promise<void>;
  onError: (v: string) => void;
}): ReactElement {
  const [year, setYear] = useState(new Date().getUTCFullYear());
  const [month, setMonth] = useState(new Date().getUTCMonth() + 1);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [auditPeriod, setAuditPeriod] = useState('');

  const tiles = useMemo(() => {
    const counts = { Open: 0, SoftClosed: 0, HardClosed: 0 };
    for (const p of periods) {
      if (p.status in counts) {
        counts[p.status as keyof typeof counts] += 1;
      }
    }
    return counts;
  }, [periods]);

  const createPeriod = async (e: FormEvent) => {
    e.preventDefault();
    onError('');
    try {
      const res = await authFetch(PERIODS_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year, month, operatingRegion: 'Permian' })
      });
      await readJson<PeriodRow>(res);
      await onReload();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Create period failed');
    }
  };

  const setStatus = async (id: string, status: string) => {
    onError('');
    try {
      const res = await authFetch(`${PERIODS_API_URL}/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      await readJson<PeriodRow>(res);
      await onReload();
      const rows = await readJson<AuditRow[]>(await authFetch(`${PERIODS_API_URL}/${id}/audit`));
      setAudit(rows);
      setAuditPeriod(id);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Update period failed');
    }
  };

  return (
    <section>
      <div className="tiles-grid">
        <div className="tile">
          <h3>{tiles.Open}</h3>
          <p>Open</p>
        </div>
        <div className="tile">
          <h3>{tiles.SoftClosed}</h3>
          <p>Soft-closed</p>
        </div>
        <div className="tile">
          <h3>{tiles.HardClosed}</h3>
          <p>Hard-closed</p>
        </div>
      </div>
      {canUpdate && (
        <form className="grid" onSubmit={createPeriod}>
          <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
          <input type="number" min={1} max={12} value={month} onChange={(e) => setMonth(Number(e.target.value))} />
          <button type="submit" data-testid="period-create">
            Open month
          </button>
        </form>
      )}
      <div className="requests-grid-wrap">
        <table className="requests-grid" data-testid="periods-grid">
          <thead>
            <tr>
              <th>Month</th>
              <th>Production</th>
              <th>Payment</th>
              <th>Region</th>
              <th>Status</th>
              {canUpdate && <th></th>}
            </tr>
          </thead>
          <tbody>
            {periods.length === 0 && (
              <tr>
                <td colSpan={canUpdate ? 6 : 5}>No periods</td>
              </tr>
            )}
            {periods.map((p) => (
              <tr
                key={p.id}
                className={auditPeriod === p.id ? 'selected' : undefined}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.currentTarget.click();
                  }
                }}
                onClick={() => {
                  void (async () => {
                    try {
                      const rows = await readJson<AuditRow[]>(await authFetch(`${PERIODS_API_URL}/${p.id}/audit`));
                      setAudit(rows);
                      setAuditPeriod(p.id);
                    } catch (err) {
                      onError(err instanceof Error ? err.message : 'Load period activity failed');
                    }
                  })();
                }}
              >
                <td>{p.periodId}</td>
                <td>{p.productionDate}</td>
                <td>{p.paymentDate}</td>
                <td>{p.operatingRegion}</td>
                <td>
                  <span className={statusClass(p.status)}>{p.status}</span>
                </td>
                {canUpdate && (
                  <td>
                    {p.status === 'Open' && (
                      <button type="button" data-testid={`period-soft-close-${p.periodId}`} onClick={() => void setStatus(p.id, 'SoftClosed')}>
                        Soft-close
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {auditPeriod && (
        <ActivityTable
          testId="period-audit"
          rows={audit.map((row) =>
            activityFromAudit({
              id: row.id,
              actor: row.actor,
              occurredAtUtc: row.occurredAtUtc,
              action: row.action,
              beforeJson: row.beforeJson,
              afterJson: row.afterJson
            })
          )}
        />
      )}
    </section>
  );
}

function ComparePanel({
  authFetch,
  onSelectWell,
  selectedWell
}: {
  authFetch: AuthFetch;
  onSelectWell: (well: WellHeader) => void;
  selectedWell: WellHeader | null;
}): ReactElement {
  const [data, setData] = useState<CompareResult | null>(null);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('Rounding');

  const load = useCallback(async () => {
    setError('');
    try {
      setData(await readJson<CompareResult>(await authFetch(`${EXCALIBUR_API_URL}/compare?pilot=true`)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Compare failed');
    }
  }, [authFetch]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!selectedWell && data?.wells[0]) {
      onSelectWell(data.wells[0]);
    }
  }, [data, onSelectWell, selectedWell]);

  const explain = async (line: CompareLine) => {
    await readJson(
      await authFetch(`${EXCALIBUR_API_URL}/compare/explain`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wellApi: line.wellApi,
          externalKey: line.externalKey,
          interestClass: line.interestClass,
          status: line.status,
          reasonCode: reason
        })
      })
    );
    await load();
  };

  const rows = data?.lines ?? [];
  return (
    <section>
      {error && <p className="error">{error}</p>}
      <p data-testid="unexplained-count">Unexplained: {data?.unexplainedCount ?? 0}</p>
      <label>
        Reason
        <input data-testid="explain-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      </label>
      <div className="requests-grid-wrap">
        <table className="requests-grid" data-testid="doi-compare-grid">
          <thead>
            <tr>
              <th>Well</th>
              <th>Status</th>
              <th>Owner key</th>
              <th>Class</th>
              <th>Ours</th>
              <th>Excalibur</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={7}>No compare lines</td>
              </tr>
            )}
            {rows.map((line) => (
              <tr key={`${line.wellApi}-${line.externalKey}-${line.status}`}>
                <td>
                  <button
                    type="button"
                    className="linkish"
                    onClick={() => {
                      const well = data?.wells.find((w) => w.id === line.wellId);
                      if (well) {
                        onSelectWell(well);
                      }
                    }}
                  >
                    {line.wellApi}
                  </button>
                </td>
                <td>
                  <span className="status-pill">{line.status}</span>
                </td>
                <td>{line.externalKey}</td>
                <td>{line.interestClass}</td>
                <td>{line.oursDecimal ?? '—'}</td>
                <td>{line.excaliburDecimal ?? '—'}</td>
                <td>
                  {line.status !== 'Match' && !line.explained && (
                    <button type="button" data-testid={`explain-${line.wellApi}`} onClick={() => void explain(line)}>
                      Mark explained
                    </button>
                  )}
                  {line.explained && line.reasonCode}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function IngestPanel({
  authFetch,
  periodId,
  onError
}: {
  authFetch: AuthFetch;
  periodId: string;
  onError: (v: string) => void;
}): ReactElement {
  const [batches, setBatches] = useState<IngestBatch[]>([]);

  const run = async (kind: 'names' | 'wells' | 'doi') => {
    onError('');
    const body = kind === 'names' ? NAME_FIXTURE : kind === 'wells' ? WELL_FIXTURE : DOI_FIXTURE;
    const res = await authFetch(`${EXCALIBUR_API_URL}/ingest/${kind}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, periodId: periodId || null })
    });
    const batch = await readJson<IngestBatch>(res);
    setBatches((prev) => [batch, ...prev]);
  };

  return (
    <section className="card">
      <h2>Excalibur ingest</h2>
      <p>Loads the Phase 0 fixture files. A second run is idempotent.</p>
      <div className="grid">
        <button type="button" data-testid="ingest-names" onClick={() => void run('names').catch((e) => onError(String(e)))}>
          Ingest names
        </button>
        <button type="button" data-testid="ingest-wells" onClick={() => void run('wells').catch((e) => onError(String(e)))}>
          Ingest wells
        </button>
        <button type="button" data-testid="ingest-doi" onClick={() => void run('doi').catch((e) => onError(String(e)))}>
          Ingest DOI snapshot
        </button>
      </div>
      <table className="requests-grid" data-testid="ingest-batches">
        <thead>
          <tr>
            <th>Kind</th>
            <th>Accepted</th>
            <th>Exceptions</th>
          </tr>
        </thead>
        <tbody>
          {batches.length === 0 && (
            <tr>
              <td colSpan={3}>No ingest jobs in this session</td>
            </tr>
          )}
          {batches.map((b) => (
            <tr key={b.id}>
              <td>{b.kind}</td>
              <td>{b.acceptedCount}</td>
              <td>
                {b.exceptionCount}
                {b.exceptions.map((x) => (
                  <div key={x.externalKey}>
                    {x.externalKey}: {x.reason}
                  </div>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
